
<template>
  <div class="w-full max-w-lg mx-auto min-w-0">
    <!-- Loading State with Enhanced Animation -->
    <div 
      v-if="loading" 
      class="flex items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl aspect-square border-2 border-dashed border-gray-300 relative overflow-hidden"
    >
      <!-- Animated Background -->
      <div class="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-blue-500/10 animate-pulse"></div>
      
      <div class="text-center relative z-10">
        <!-- Enhanced Loading Spinner -->
        <div class="relative">
          <LoadingSpinner class="w-16 h-16 mx-auto mb-4 text-blue-500" />
          <!-- Generation source indicator -->
          <div class="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
               :class="generationSource === 'frontend' ? 'bg-emerald-500 text-white' : 'bg-blue-500 text-white'">
            {{ generationSource === 'frontend' ? '⚡' : '🚀' }}
          </div>
        </div>
        
        <p class="text-gray-600 font-medium mb-2">
          Generating QR Code...
        </p>
        <p class="text-sm text-gray-500">
          Using {{ generationSource }} 
          <span v-if="realtimePreview" class="text-emerald-600">(Real-time)</span>
        </p>
        
        <!-- Progress indicator for longer operations -->
        <div v-if="isLongOperation" class="mt-4 w-full bg-gray-200 rounded-full h-2">
          <div class="bg-blue-500 h-2 rounded-full animate-pulse" :style="{ width: loadingProgress + '%' }"></div>
        </div>
      </div>
    </div>

    <!-- QR Code Display with Enhanced Features -->
    <div 
      v-else-if="qrCode" 
      class="bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-2xl"
    >
      <!-- QR Code Header -->
      <div class="bg-gradient-to-r from-gray-50 to-blue-50 px-4 tablet:px-6 py-4 border-b border-gray-100">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-2">
            <QrCodeIcon class="w-5 h-5 text-gray-600" />
            <span class="font-medium text-gray-900">QR Code</span>
          </div>
          
          <!-- Source Badge -->
          <div class="flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium"
               :class="getSourceBadgeClass(qrCode.source || generationSource)">
            <span class="w-1.5 h-1.5 rounded-full" 
                  :class="getSourceDotClass(qrCode.source || generationSource)"></span>
            <span>{{ qrCode.source || generationSource }}</span>
            <span v-if="realtimePreview && (qrCode.source || generationSource) === 'frontend'" 
                  class="text-emerald-600">●</span>
          </div>
        </div>
      </div>

      <!-- QR Code Image Container -->
      <div class="p-4 tablet:p-8 bg-white relative">
        <!-- QR Code with Enhanced Display -->
        <div class="relative group">
          <img 
            ref="qrImage"
            :src="getQRImageSource()"
            :alt="`QR Code for ${qrCode.contentType || 'content'}`"
            class="mx-auto rounded-lg shadow-sm max-w-full h-auto transition-transform duration-300 group-hover:scale-105"
            :style="{ maxWidth: qrDisplaySize + 'px' }"
            @load="onImageLoad"
            @error="onImageError"
          />
          
          <!-- Overlay Actions (visible on hover; touch-friendly 44px) -->
          <div class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex flex-col tablet:flex-row items-center justify-center gap-2 tablet:gap-3 tablet:space-x-3">
            <button
              type="button"
              @click="zoomIn"
              class="flex items-center justify-center min-w-[44px] min-h-[44px] p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
              title="Zoom In"
            >
              <MagnifyingGlassPlusIcon class="w-5 h-5 text-gray-700" />
            </button>
            <button
              type="button"
              @click="downloadQR('png')"
              class="flex items-center justify-center min-w-[44px] min-h-[44px] p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
              title="Download PNG"
            >
              <ArrowDownTrayIcon class="w-5 h-5 text-gray-700" />
            </button>
            <button
              type="button"
              @click="copyToClipboard"
              class="flex items-center justify-center min-w-[44px] min-h-[44px] p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
              title="Copy to Clipboard"
            >
              <ClipboardIcon class="w-5 h-5 text-gray-700" />
            </button>
          </div>
          
          <!-- Real-time indicator -->
          <div v-if="realtimePreview && (qrCode.source || generationSource) === 'frontend'" 
               class="absolute top-2 right-2 w-3 h-3 bg-emerald-400 rounded-full animate-pulse border-2 border-white shadow-lg">
          </div>
        </div>
      </div>

      <!-- QR Code Information -->
      <div class="px-4 tablet:px-6 py-4 bg-gray-50 border-t border-gray-100">
        <div class="grid grid-cols-1 tablet:grid-cols-2 gap-4 text-sm">
          <!-- Content Type -->
          <div>
            <span class="font-medium text-gray-600">Type:</span>
            <span class="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                  :class="getContentTypeClass(qrCode.contentType)">
              {{ formatContentType(qrCode.contentType) }}
            </span>
          </div>
          
          <!-- File Size -->
          <div v-if="qrCode.metadata?.fileSize">
            <span class="font-medium text-gray-600">Size:</span>
            <span class="ml-2 text-gray-900">{{ formatFileSize(qrCode.metadata.fileSize) }}</span>
          </div>
          
          <!-- Dimensions -->
          <div v-if="qrDimensions">
            <span class="font-medium text-gray-600">Dimensions:</span>
            <span class="ml-2 text-gray-900">{{ qrDimensions.width }}×{{ qrDimensions.height }}</span>
          </div>
          
          <!-- Generation Time -->
          <div v-if="qrCode.metadata?.createdAt">
            <span class="font-medium text-gray-600">Created:</span>
            <span class="ml-2 text-gray-900">{{ formatDate(qrCode.metadata.createdAt) }}</span>
          </div>
        </div>
        
        <!-- Additional Metadata -->
        <div v-if="qrCode.metadata?.errorCorrectionLevel" class="mt-3 pt-3 border-t border-gray-200">
          <div class="flex items-center justify-between text-xs text-gray-500">
            <span>Error Correction: {{ qrCode.metadata.errorCorrectionLevel }}</span>
            <span v-if="qrCode.metadata?.version">Version: {{ qrCode.metadata.version }}</span>
          </div>
        </div>
      </div>

      <!-- Quick Actions Bar (stack on mobile, row on tablet/desktop; 44px touch targets) -->
      <div class="px-4 tablet:px-6 py-4 bg-white border-t border-gray-100">
        <div class="flex flex-col tablet:flex-row tablet:flex-wrap justify-center gap-2 tablet:gap-0 tablet:space-x-2">
          <!-- Download Actions -->
          <div class="flex flex-wrap justify-center gap-2 tablet:space-x-1">
            <button
              v-for="format in downloadFormats"
              :key="format.type"
              type="button"
              @click="downloadQR(format.type)"
              class="inline-flex items-center justify-center min-h-[44px] px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              :title="`Download as ${format.label}`"
            >
              <component :is="format.icon" class="w-4 h-4 mr-1" />
              {{ format.label }}
            </button>
          </div>
          <!-- Additional Actions -->
          <div class="flex flex-wrap justify-center gap-2 tablet:space-x-1 tablet:border-l tablet:border-gray-200 tablet:pl-2">
            <button
              type="button"
              @click="copyToClipboard"
              class="inline-flex items-center justify-center min-h-[44px] px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              title="Copy to clipboard"
            >
              <ClipboardIcon class="w-4 h-4 mr-1" />
              Copy
            </button>
            <button
              v-if="canShare"
              type="button"
              @click="shareQR"
              class="inline-flex items-center justify-center min-h-[44px] px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              title="Share QR code"
            >
              <ShareIcon class="w-4 h-4 mr-1" />
              Share
            </button>
            <button
              type="button"
              @click="printQR"
              class="inline-flex items-center justify-center min-h-[44px] px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              title="Print QR code"
            >
              <PrinterIcon class="w-4 h-4 mr-1" />
              Print
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Enhanced Empty State -->
    <div 
      v-else 
      class="flex items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50/50 rounded-xl aspect-square border-2 border-dashed border-gray-300 relative overflow-hidden group hover:border-blue-400 transition-colors duration-300"
    >
      <!-- Animated Background Pattern -->
      <div class="absolute inset-0 opacity-5">
        <div class="absolute inset-0 bg-grid-pattern"></div>
      </div>
      
      <div class="text-center relative z-10">
        <div class="relative mb-4">
          <QrCodeIcon class="w-20 h-20 mx-auto text-gray-300 group-hover:text-blue-400 transition-colors duration-300" />
          <!-- Floating animation elements -->
          <div class="absolute -top-2 -right-2 w-4 h-4 bg-blue-200 rounded-full animate-bounce opacity-60"></div>
          <div class="absolute -bottom-1 -left-1 w-3 h-3 bg-purple-200 rounded-full animate-pulse opacity-60"></div>
        </div>
        
        <h3 class="text-lg font-medium text-gray-900 mb-2">
          Ready to Generate
        </h3>
        <p class="text-gray-500 mb-4">
          Your QR code will appear here once generated
        </p>
        
        <!-- Service Status Indicator -->
        <div class="flex items-center justify-center space-x-2 text-sm">
          <div class="flex items-center space-x-1">
            <div class="w-2 h-2 rounded-full" 
                 :class="generationSource === 'frontend' ? 'bg-emerald-400' : 'bg-blue-400'"></div>
            <span class="text-gray-600">{{ generationSource }} ready</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Zoom Modal -->
    <div v-if="showZoomModal" 
         @click="closeZoom"
         class="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div class="relative max-w-4xl max-h-full">
        <img 
          :src="getQRImageSource()"
          class="max-w-full max-h-full rounded-lg shadow-2xl"
          @click.stop
        />
        <button 
          @click="closeZoom"
          class="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
        >
          <XMarkIcon class="w-6 h-6" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { 
  QrCodeIcon, 
  ArrowDownTrayIcon, 
  ClipboardIcon,
  MagnifyingGlassPlusIcon,
  ShareIcon,
  PrinterIcon,
  XMarkIcon,
  PhotoIcon,
  DocumentIcon
} from '@heroicons/vue/24/outline'
import LoadingSpinner from '../common/LoadingSpinner.vue'
import { useToast } from 'vue-toastification'
import { formatDistanceToNow } from 'date-fns'

export default {
  name: 'QRDisplay',
  components: {
    QrCodeIcon,
    ArrowDownTrayIcon,
    ClipboardIcon,
    MagnifyingGlassPlusIcon,
    ShareIcon,
    PrinterIcon,
    XMarkIcon,
    PhotoIcon,
    DocumentIcon,
    LoadingSpinner
  },
  props: {
    qrCode: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    realtimePreview: {
      type: Boolean,
      default: false
    },
    generationSource: {
      type: String,
      default: 'frontend'
    }
  },

  
  emits: ['download'],
  setup(props, { emit }) {
    const toast = useToast()
    const qrImage = ref(null)
    const showZoomModal = ref(false)
    const qrDimensions = ref(null)
    const qrDisplaySize = ref(400)
    const loadingProgress = ref(0)
    const isLongOperation = ref(false)

    // Computed properties
    const downloadFormats = computed(() => [
      { type: 'png', label: 'PNG', icon: PhotoIcon },
      { type: 'jpeg', label: 'JPEG', icon: PhotoIcon },
      { type: 'svg', label: 'SVG', icon: DocumentIcon }
    ])

    const canShare = computed(() => {
      return navigator.share && props.qrCode
    })

    // Loading progress simulation
    let loadingTimer = null
    watch(() => props.loading, (newLoading) => {
      if (newLoading) {
        loadingProgress.value = 0
        isLongOperation.value = false
        
        loadingTimer = setTimeout(() => {
          isLongOperation.value = true
          const interval = setInterval(() => {
            if (loadingProgress.value < 90) {
              loadingProgress.value += Math.random() * 10
            }
          }, 200)
          
          // Clear interval when loading stops
          const stopWatcher = watch(() => props.loading, (stillLoading) => {
            if (!stillLoading) {
              clearInterval(interval)
              loadingProgress.value = 100
              setTimeout(() => {
                isLongOperation.value = false
                loadingProgress.value = 0
              }, 500)
              stopWatcher()
            }
          })
        }, 2000)
      } else {
        if (loadingTimer) {
          clearTimeout(loadingTimer)
          loadingTimer = null
        }
      }
    })

    console.log('QRDisplay component initialized', props.qrCode)

    // Methods
    const formatFileSize = (bytes) => {
      if (!bytes) return 'Unknown'
      const units = ['B', 'KB', 'MB', 'GB']
      let size = bytes
      let unitIndex = 0

      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
      }

      return `${size.toFixed(1)} ${units[unitIndex]}`
    }

    const formatDate = (date) => {
      if (!date) return ''
      return formatDistanceToNow(new Date(date), { addSuffix: true })
    }

    const formatContentType = (type) => {
      if (!type) return 'Unknown'
      return type.charAt(0).toUpperCase() + type.slice(1)
    }

    const getSourceBadgeClass = (source) => {
      return source === 'frontend' 
        ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
        : 'bg-blue-100 text-blue-800 border border-blue-200'
    }

    const getSourceDotClass = (source) => {
      return source === 'frontend' ? 'bg-emerald-400' : 'bg-blue-400'
    }

    const getContentTypeClass = (type) => {
      const classes = {
        'text': 'bg-gray-100 text-gray-800',
        'url': 'bg-blue-100 text-blue-800',
        'vcard': 'bg-green-100 text-green-800',
        'wifi': 'bg-purple-100 text-purple-800',
        'sms': 'bg-yellow-100 text-yellow-800',
        'email': 'bg-red-100 text-red-800'
      }
      return classes[type] || 'bg-gray-100 text-gray-800'
    }

    const getQRImageSource = () => {
      if (!props.qrCode) return ''
      return props.qrCode.qrCode || props.qrCode.qrCodeData || props.qrCode.dataURL
    }

    const onImageLoad = () => {
      if (qrImage.value) {
        qrDimensions.value = {
          width: qrImage.value.naturalWidth,
          height: qrImage.value.naturalHeight
        }
      }
    }

    const onImageError = () => {
      toast.error('Failed to load QR code image')
    }

    const downloadQR = (format) => {
      emit('download', format)
    }

    const copyToClipboard = async () => {
      if (!props.qrCode) return

      try {
        const imageSource = getQRImageSource()
        
        if (imageSource.startsWith('data:')) {
          // Convert data URL to blob
          const response = await fetch(imageSource)
          const blob = await response.blob()
          
          // Copy to clipboard
          await navigator.clipboard.write([
            new ClipboardItem({ [blob.type]: blob })
          ])
          
          toast.success('QR code copied to clipboard!', { icon: '📋' })
        } else {
          // For regular URLs, copy the image
          const img = new Image()
          img.crossOrigin = 'anonymous'
          img.onload = async () => {
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')
            canvas.width = img.width
            canvas.height = img.height
            ctx.drawImage(img, 0, 0)
            
            canvas.toBlob(async (blob) => {
              await navigator.clipboard.write([
                new ClipboardItem({ [blob.type]: blob })
              ])
              toast.success('QR code copied to clipboard!', { icon: '📋' })
            })
          }
          img.src = imageSource
        }
      } catch (error) {
        // Fallback: copy the data URL as text
        try {
          await navigator.clipboard.writeText(getQRImageSource())
          toast.success('QR code URL copied to clipboard!', { icon: '🔗' })
        } catch (fallbackError) {
          toast.error('Failed to copy to clipboard')
        }
      }
    }

    const shareQR = async () => {
      if (!canShare.value) return

      try {
        const imageSource = getQRImageSource()
        const response = await fetch(imageSource)
        const blob = await response.blob()
        const file = new File([blob], 'qrcode.png', { type: blob.type })

        await navigator.share({
          title: 'QR Code',
          text: `QR Code (${props.qrCode.contentType})`,
          files: [file]
        })
        
        toast.success('QR code shared successfully!', { icon: '📤' })
      } catch (error) {
        if (error.name !== 'AbortError') {
          toast.error('Failed to share QR code')
        }
      }
    }

    const printQR = () => {
      if (!props.qrCode) return

      const printWindow = window.open('', '_blank')
      const imageSource = getQRImageSource()
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>QR Code - ${props.qrCode.contentType}</title>
            <style>
              body {
                margin: 0;
                padding: 20px;
                display: flex;
                flex-direction: column;
                align-items: center;
                font-family: Arial, sans-serif;
              }
              .qr-container {
                text-align: center;
                margin-bottom: 20px;
              }
              .qr-image {
                max-width: 400px;
                height: auto;
                border: 1px solid #ddd;
                border-radius: 8px;
              }
              .qr-info {
                margin-top: 20px;
                font-size: 14px;
                color: #666;
              }
              @media print {
                body { margin: 0; padding: 10px; }
                .qr-image { max-width: 300px; }
              }
            </style>
          </head>
          <body>
            <div class="qr-container">
              <h2>QR Code</h2>
              <img src="${imageSource}" alt="QR Code" class="qr-image" />
              <div class="qr-info">
                <p><strong>Type:</strong> ${formatContentType(props.qrCode.contentType)}</p>
                <p><strong>Generated:</strong> ${formatDate(props.qrCode.metadata?.createdAt)}</p>
                <p><strong>Source:</strong> ${props.qrCode.source || props.generationSource}</p>
              </div>
            </div>
          </body>
        </html>
      `)
      
      printWindow.document.close()
      printWindow.onload = () => {
        printWindow.print()
        setTimeout(() => printWindow.close(), 1000)
      }
      
      toast.success('Print dialog opened!', { icon: '🖨️' })
    }

    const zoomIn = () => {
      showZoomModal.value = true
    }

    const closeZoom = () => {
      showZoomModal.value = false
    }

    // Keyboard shortcuts
    const handleKeydown = (event) => {
      if (!props.qrCode) return
      
      switch (event.key) {
        case 'c':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault()
            copyToClipboard()
          }
          break
        case 'd':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault()
            downloadQR('png')
          }
          break
        case 'p':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault()
            printQR()
          }
          break
        case 'Escape':
          if (showZoomModal.value) {
            closeZoom()
          }
          break
      }
    }

    onMounted(() => {
      document.addEventListener('keydown', handleKeydown)
    })

    return {
      toast,
      qrImage,
      showZoomModal,
      qrDimensions,
      qrDisplaySize,
      loadingProgress,
      isLongOperation,
      downloadFormats,
      canShare,
      formatFileSize,
      formatDate,
      formatContentType,
      getSourceBadgeClass,
      getSourceDotClass,
      getContentTypeClass,
      getQRImageSource,
      onImageLoad,
      onImageError,
      downloadQR,
      copyToClipboard,
      shareQR,
      printQR,
      zoomIn,
      closeZoom
    }
  }
}
</script>

<style scoped>
/* Background grid pattern for empty state */
.bg-grid-pattern {
  background-image: 
    linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px);
  background-size: 20px 20px;
}

/* Enhanced hover effects */
.group:hover .group-hover\:scale-105 {
  transform: scale(1.05);
}

.group:hover .group-hover\:text-blue-400 {
  color: #60a5fa;
}

/* Loading spinner animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Smooth transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

/* Enhanced shadow effects */
.shadow-xl {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.hover\:shadow-2xl:hover {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

/* Button hover effects */
.hover\:bg-gray-50:hover {
  background-color: #f9fafb;
}

.hover\:bg-white:hover {
  background-color: #ffffff;
}

/* Focus styles for accessibility */
.focus\:ring-2:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px #3b82f6;
}

.focus\:ring-offset-2:focus {
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #3b82f6;
}

/* Zoom modal styles */
.fixed {
  position: fixed;
}

.inset-0 {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.z-50 {
  z-index: 50;
}

/* Animation for floating elements */
@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
    animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
  }
  50% {
    transform: translateY(-25%);
    animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
  }
}

.animate-bounce {
  animation: bounce 1s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Progress bar styles */
.bg-gray-200 {
  background-color: #e5e7eb;
}

.bg-blue-500 {
  background-color: #3b82f6;
}

.rounded-full {
  border-radius: 9999px;
}

/* Responsive design (mobile < 640px) */
@media (max-width: 639px) {
  .p-8 {
    padding: 1.5rem;
  }
  
  .px-6 {
    padding-left: 1rem;
    padding-right: 1rem;
  }
  
  .py-4 {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }
  
  .space-x-2 > :not([hidden]) ~ :not([hidden]) {
    margin-left: 0.25rem;
  }
  
  .text-sm {
    font-size: 0.75rem;
  }
  
  .px-3 {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
  
  .py-2 {
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .bg-white {
    background-color: #1f2937;
  }
  
  .text-gray-900 {
    color: #f9fafb;
  }
  
  .text-gray-700 {
    color: #d1d5db;
  }
  
  .text-gray-600 {
    color: #9ca3af;
  }
  
  .text-gray-500 {
    color: #6b7280;
  }
  
  .border-gray-200 {
    border-color: #374151;
  }
  
  .border-gray-100 {
    border-color: #4b5563;
  }
  
  .bg-gray-50 {
    background-color: #111827;
  }
  
  .bg-gray-100 {
    background-color: #1f2937;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .border-gray-200 {
    border-color: #000000;
    border-width: 2px;
  }
  
  .text-gray-600 {
    color: #000000;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .animate-bounce,
  .animate-pulse {
    animation: none;
  }
  
  .transition-all,
  .transition-colors,
  .transition-transform {
    transition: none;
  }
}

/* Print styles */
@media print {
  .bg-gradient-to-r,
  .bg-gradient-to-br {
    background: #ffffff !important;
    color: #000000 !important;
  }
  
  .shadow-xl,
  .shadow-2xl {
    box-shadow: none !important;
  }
  
  button {
    display: none !important;
  }
}
</style>